import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { User, LoginCredentials, RegisterData } from '@/types'
import { authService } from '@/services/authService'
import { on as onEvent } from '@/services/events'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  phoneLogin: (phone: string, code: string) => Promise<void>
  phoneRegister: (phone: string, code: string, username?: string, couponCode?: string, referralCode?: string) => Promise<void>
  logout: () => void
  refreshToken: () => Promise<void>
  refreshUser: () => Promise<void> // 🔥 修复：刷新用户信息（包括积分）
  updateUser: (user: Partial<User>) => void
  clearError: () => void
  setLoading: (loading: boolean) => void
}

type AuthStore = AuthState & AuthActions

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // 初始状态
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (credentials: LoginCredentials) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await authService.login(credentials)
          
          if (response.data) {
            set({
              user: response.data.user,
              token: response.data.token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.message || '登录失败',
            isAuthenticated: false
          })
          throw error
        }
      },

      register: async (data: RegisterData) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await authService.register(data)
          
          if (response.data) {
            set({
              user: response.data.user,
              token: response.data.token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.message || '注册失败',
            isAuthenticated: false
          })
          throw error
        }
      },

      phoneLogin: async (phone: string, code: string) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await authService.phoneLogin(phone, code)
          
          if (response.data) {
            set({
              user: response.data.user,
              token: response.data.token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.message || '登录失败',
            isAuthenticated: false
          })
          throw error
        }
      },

      phoneRegister: async (phone: string, code: string, username?: string, couponCode?: string, referralCode?: string) => {
        try {
          set({ isLoading: true, error: null })
          
          const response = await authService.phoneRegister(phone, code, username, couponCode, referralCode)
          
          if (response.data) {
            set({
              user: response.data.user,
              token: response.data.token,
              isAuthenticated: true,
              isLoading: false,
              error: null
            })
          }
        } catch (error: any) {
          set({
            isLoading: false,
            error: error.response?.data?.message || '注册失败',
            isAuthenticated: false
          })
          throw error
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null
        })
        
        // 清除本地存储
        localStorage.removeItem('auth-storage')
        
        // 清理积分活动数据
        try {
          // 动态导入避免循环依赖
          import('./creditsActivityStore').then(({ useCreditsActivityStore }) => {
            useCreditsActivityStore.getState().clearAll()
          }).catch((error) => {
            console.error('Failed to clear credits activity:', error)
          })
        } catch (error) {
          console.error('Failed to clear credits activity:', error)
        }
        
        // 调用后端登出接口（可选）
        authService.logout().catch(console.error)
      },

      refreshToken: async () => {
        try {
          const { token } = get()
          if (!token) {
            throw new Error('No token available')
          }

          const response = await authService.refreshToken()
          
          if (response.data) {
            set({
              token: response.data.token,
              isAuthenticated: true
            })
          }
        } catch (error) {
          // Token刷新失败，清除认证状态
          get().logout()
          throw error
        }
      },

      // 🔥 修复：刷新用户信息（包括积分）
      refreshUser: async () => {
        try {
          const response = await authService.getCurrentUser()
          if (response.data?.user) {
            set({
              user: response.data.user,
              isAuthenticated: true
            })
            console.log('✅ [authStore] 用户信息已刷新，积分:', response.data.user.pointsBalance)
          }
        } catch (error) {
          console.error('❌ [authStore] 刷新用户信息失败:', error)
          throw error
        }
      },

      updateUser: (userData: Partial<User>) => {
        const { user } = get()
        if (user) {
          set({
            user: { ...user, ...userData }
          })
        }
      },

      clearError: () => {
        set({ error: null })
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      }
    }),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated
      })
    }
  )
)

// 初始化时检查token有效性
export const initializeAuth = async () => {
  const { token, refreshToken, logout, setLoading } = useAuthStore.getState()
  
  if (token) {
    try {
      setLoading(true)
      // 验证token是否有效并获取最新的用户信息
      const response = await authService.getCurrentUser()
      
      if (response.data?.user) {
        // 更新用户信息，包括积分余额
        useAuthStore.setState({ 
          user: response.data.user,
          isAuthenticated: true 
        })
      }
    } catch (error) {
      // Token无效，尝试刷新
      try {
        await refreshToken()
      } catch (refreshError) {
        // 刷新失败，清除认证状态
        logout()
      }
    } finally {
      setLoading(false)
    }
  }
}

// 实时事件：积分与用户资料变更
onEvent('credits_updated', ({ userId, pointsBalance, delta }) => {
  const { user, isAuthenticated } = useAuthStore.getState()
  if (!isAuthenticated || !user || user.id !== userId) return
  
  // 优先使用 pointsBalance，其次使用增量计算
  const nextBalance = typeof pointsBalance === 'number'
    ? pointsBalance
    : (user.pointsBalance || 0) + (delta || 0)
    
  console.log('🔄 [authStore] 更新积分余额:', { userId, pointsBalance, delta, nextBalance })
  useAuthStore.setState({ user: { ...user, pointsBalance: nextBalance } })
})

onEvent('user_updated', (payload: any) => {
  const { user, isAuthenticated } = useAuthStore.getState()
  if (!isAuthenticated || !user || user.id !== payload?.id) return
  useAuthStore.setState({ user: { ...user, ...payload } })
})
